// Phase 8 R4 — client-side recent canned jobs (per shop).
// Pure localStorage; never hits the network. Not learning, not analytics.

import { useCallback, useEffect, useState } from 'react';

const MAX_STORED = 8;

function storageKey(shopId: string) {
  return `optibay:recent-canned-jobs:${shopId}`;
}

function readStorage(shopId: string): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(storageKey(shopId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string').slice(0, MAX_STORED);
  } catch {
    return [];
  }
}

function writeStorage(shopId: string, ids: string[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(shopId), JSON.stringify(ids.slice(0, MAX_STORED)));
  } catch {
    // ignore quota / privacy mode errors — recents are best-effort
  }
}

export function useRecentCannedJobs(shopId: string | undefined) {
  const [recentIds, setRecentIds] = useState<string[]>(() =>
    shopId ? readStorage(shopId) : [],
  );

  // Reload when the active shop changes.
  useEffect(() => {
    if (!shopId) {
      setRecentIds([]);
      return;
    }
    setRecentIds(readStorage(shopId));
  }, [shopId]);

  const pushRecent = useCallback(
    (id: string) => {
      if (!shopId || !id) return;
      setRecentIds((prev) => {
        const next = [id, ...prev.filter((x) => x !== id)].slice(0, MAX_STORED);
        writeStorage(shopId, next);
        return next;
      });
    },
    [shopId],
  );

  const clear = useCallback(() => {
    if (!shopId) return;
    setRecentIds([]);
    writeStorage(shopId, []);
  }, [shopId]);

  return { recentIds, pushRecent, clear };
}

// Side-channel writer for hooks that don't render a component (e.g. mutation
// onSuccess in useApplyCannedJob). Mirrors `pushRecent` without React state.
export function pushRecentCannedJob(shopId: string, id: string) {
  if (!shopId || !id || typeof window === 'undefined') return;
  const current = readStorage(shopId);
  const next = [id, ...current.filter((x) => x !== id)].slice(0, MAX_STORED);
  writeStorage(shopId, next);
}
